import { ReactNode, useContext, useMemo } from 'react';
import { DateValue } from '@givebutter/primitives/date-picker';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { faCalendar } from '@fortawesome/pro-regular-svg-icons';
import { getDatetimeRangeFromUpstream } from 'assets/js/pages/reports/util';
import { DateOperatorOptions } from 'components/filter/DateOperatorOptions';
import { NegationOptions } from 'components/filter/NegationOptions';
import { RelativeDateOperatorOptions } from 'components/filter/RelativeDateOperatorOptions';
import { FilterContainer } from 'components/filter/filters-next/FilterContainer';
import { FilterContext } from 'components/filter/filters-next/FilterContext';
import { FilterForm } from 'components/filter/filters-next/FilterForm';
import { FilterFormContext } from 'components/filter/filters-next/FilterFormContext';
import { FilterSubmit } from 'components/filter/filters-next/FilterSubmit';
import { ReadableFilterValue } from 'components/filter/filters-next/ReadableFilterValue';
import { operatorClassName } from 'components/filter/filters-next/props';
import {
    transformDateOperatorToReadable,
    transformDateToReadable,
} from 'components/filter/filters-next/readable';
import {
    getResourceFromRelationalKey,
    hasPossibleNegationOperator,
} from 'components/filter/filters-next/relational';
import { useCurrentFilter } from 'components/filter/filters-next/useCurrentFilter';
import { useFilter } from 'components/filter/filters-next/useFilter';
import {
    isRelativeDateOperator,
    transformDateToRequest,
    transformInboundDate,
} from 'components/filter/filters-next/util';
import {
    ContentFilterComponent,
    DownstreamFilterTransform,
    DownstreamPreprocessTransform,
    FilterFormValue,
    FilterIcon,
    FilterOperator,
    LabelFilterComponent,
    ReadableFilterComponent,
    RelativeDateRangeOperator,
    ServerKey,
    UpstreamFilterTransform,
} from 'components/filter/types';
import { ValidationTextMap, validateFilterDate } from 'components/filter/validations';
import * as Form from 'components/form';
import { identity } from 'lodash';
import { DateTime } from 'luxon';
import { twJoin } from 'tailwind-merge';

interface DateFilterValue {
    value: DateValue;
}

interface DynamicProps {
    label?: ReactNode;
}

interface CreatedAtFilterFormValues extends FilterFormValue<DateFilterValue> {}

export const filterKey: ServerKey[] = [
    'created_at',
    'createdAt',
    'startAt',
    'nextBillDate',
    'dob',
    'firstInstallmentDate',
    'nextInstallmentDate',
    'lastCommunicatedAt',
    'fullfillmentDate',
    'dueDate',
    'lastDonatedAt',
];

export const Icon: FilterIcon = faCalendar;

export const Filter: ContentFilterComponent = (props: DynamicProps) => {
    const { label } = props;
    const { defaultValue, ...rest } = useFilter<DateFilterValue>();
    const {
        cacheKey,
        relationalServerKey,
        strategyType: strategyTypeRaw,
    } = useContext(FilterContext);
    const strategyType = strategyTypeRaw as ServerKey;
    const relationalResource = relationalServerKey
        ? getResourceFromRelationalKey(relationalServerKey)
        : '';
    const isRelativeOptions = cacheKey === 'tableFilters' || cacheKey === 'tableFiltersBar';
    const defaultNegationOperator = relationalServerKey ? 'has' : undefined;
    const defaultFilterOperator = isRelativeOptions ? 'YTD' : 'gt';
    const methods = Form.useForm<CreatedAtFilterFormValues>({
        defaultValues: {
            value: {
                value: defaultValue?.value.value ? defaultValue.value.value : undefined,
            },
            filterOperator: defaultValue?.filterOperator
                ? defaultValue.filterOperator
                : defaultFilterOperator,
            negationOperator: defaultValue?.negationOperator
                ? defaultValue.negationOperator
                : defaultNegationOperator,
        },
        mode: 'onChange',
    });

    const { watch, path, control, setValue } = methods;
    const [operator, dateValue] = watch(['filterOperator', 'value']);
    const isCustom = operator === 'CUSTOM';

    const ctx = useMemo(
        () => ({
            defaultValue,
            ...rest,
        }),
        [defaultValue, rest],
    );

    return (
        <FilterFormContext.Provider value={ctx}>
            <Form.Provider {...methods}>
                <FilterForm>
                    <FilterContainer>
                        <Form.Container className={'gap-3'}>
                            <div className={'flex flex-col gap-2'}>
                                <div className={'flex flex-col'}>
                                    {relationalServerKey &&
                                        hasPossibleNegationOperator(
                                            strategyType,
                                            relationalServerKey,
                                        ) && (
                                            <>
                                                <Form.SingleSelect
                                                    control={control}
                                                    name="negationOperator"
                                                >
                                                    <SingleSelect.Trigger
                                                        aria-label="Negation operator"
                                                        className={operatorClassName}
                                                        size="sm"
                                                    />
                                                    <SingleSelect.Content>
                                                        <NegationOptions
                                                            resource={relationalResource}
                                                        />
                                                    </SingleSelect.Content>
                                                </Form.SingleSelect>
                                                <Form.Label
                                                    className={'sr-only'}
                                                    name={path('negationOperator')}
                                                >
                                                    Negation operator
                                                </Form.Label>
                                            </>
                                        )}

                                    <div className={'flex items-center gap-1'}>
                                        <Form.Label name={path('value.value')}>
                                            {relationalServerKey && <>of&nbsp;</>}
                                            {label ?? (
                                                <span
                                                    className={twJoin(
                                                        relationalServerKey && 'lowercase',
                                                    )}
                                                >
                                                    <Label
                                                        strategyType={strategyType}
                                                        relationalServerKey={relationalServerKey}
                                                    />
                                                </span>
                                            )}
                                        </Form.Label>
                                        <Form.SingleSelect
                                            control={control}
                                            name="filterOperator"
                                            onValueChange={(value: FilterOperator) => {
                                                if (value === 'bet' || value === 'CUSTOM') {
                                                    if (
                                                        !Array.isArray(dateValue.value) &&
                                                        DateTime.isDateTime(dateValue.value)
                                                    ) {
                                                        setValue('value.value', [
                                                            dateValue.value,
                                                            dateValue.value,
                                                        ]);
                                                    }
                                                } else if (Array.isArray(dateValue.value)) {
                                                    setValue(
                                                        'value.value',
                                                        dateValue.value[0] ?? DateTime.now(),
                                                    );
                                                }
                                            }}
                                        >
                                            <SingleSelect.Trigger
                                                aria-label="Date filter operator"
                                                className={operatorClassName}
                                                size="sm"
                                            />
                                            <SingleSelect.Content
                                                onCloseAutoFocus={e => e.preventDefault()}
                                            >
                                                {isRelativeOptions ? (
                                                    <RelativeDateOperatorOptions />
                                                ) : (
                                                    <DateOperatorOptions />
                                                )}
                                            </SingleSelect.Content>
                                        </Form.SingleSelect>
                                        <Form.Label
                                            className={'sr-only'}
                                            name={path('filterOperator')}
                                        >
                                            <Label
                                                strategyType={strategyType}
                                                relationalServerKey={relationalServerKey}
                                            />
                                        </Form.Label>
                                    </div>
                                </div>
                                {(!isRelativeOptions || isCustom) && (
                                    <Form.Container
                                        className={
                                            '[&>*]:items-center [&_.react-calendar]:w-72 [&_.react-calendar]:p-0'
                                        }
                                    >
                                        <Form.UseFormDatePicker
                                            name={'value.value'}
                                            clearable={true}
                                            control={control}
                                            selectRange={isCustom || operator === 'bet'}
                                            isTextInput={false}
                                            rules={{
                                                validate: validateFilterDate,
                                                required: ValidationTextMap.Required,
                                            }}
                                        />
                                        <Form.Error name={path('value.value')} />
                                    </Form.Container>
                                )}
                            </div>
                        </Form.Container>
                        <FilterSubmit />
                    </FilterContainer>
                </FilterForm>
            </Form.Provider>
        </FilterFormContext.Provider>
    );
};

export const Readable: ReadableFilterComponent = (props: DynamicProps) => {
    const { label } = props;
    const currentFilter = useCurrentFilter<DateFilterValue>();
    const operator = currentFilter?.filterOperator;
    const isRelative = operator ? isRelativeOperator(operator) : false;
    const value = currentFilter?.value.value ?? DateTime.now();

    return (
        <ReadableFilterValue Label={label}>
            {operator ? transformDateOperatorToReadable(operator) : ''}{' '}
            {!isRelative && transformDateToReadable(value)}
        </ReadableFilterValue>
    );
};

export const Label: LabelFilterComponent = ({ strategyType, relationalServerKey }) => {
    const { resource } = useContext(FilterContext);

    switch (strategyType) {
        case 'startAt':
            return 'Created on';
        case 'dueDate':
            return 'Due date';
        case 'nextBillDate':
            return 'Next bill date';
        case 'fullfillmentDate':
            return 'Fulfillment date';
        case 'firstInstallmentDate':
            return 'First installment date';
        case 'nextInstallmentDate':
            return 'Next installment date';
        case 'lastCommunicatedAt':
            return 'Last communication date';
        case 'lastDonatedAt':
            return 'Last donation date';
        case 'dob':
            return 'Birthday';
        case 'created_at': {
            if (relationalServerKey === 'has_campaign_members') {
                return 'Date registered';
            }
            if (resource === 'contacts' && relationalServerKey === 'has_transactions') {
                return 'Date added';
            }

            return 'Date';
        }
        case 'createdAt':
            return 'Created date';
        default:
            return 'Date';
    }
};

export const downstream: DownstreamFilterTransform<DateFilterValue> = identity;

export const preprocessDownstream: DownstreamPreprocessTransform<
    string | [string, string]
> = filter => {
    const isRelative = filter.operator && isRelativeDateOperator(filter.operator as FilterOperator);

    let value;
    if (isRelative) {
        value = getDatetimeRangeFromUpstream(filter.value as [string, string]);
    } else if (Array.isArray(filter.value)) {
        value = filter.value.map(transformInboundDate);
    } else {
        value = transformInboundDate(filter.value);
    }

    return { value };
};

export const upstream: UpstreamFilterTransform<DateFilterValue> = filter => {
    if (filter.filterOperator && isRelativeDateOperator(filter.filterOperator)) {
        const dateValue = filter.value.value;
        return {
            operator: filter.filterOperator as RelativeDateRangeOperator,
            value:
                Array.isArray(dateValue) && dateValue[0] && dateValue[1]
                    ? [dateValue[0].startOf('day'), dateValue[1].endOf('day')]
                    : dateValue,
        };
    }

    return transformDateToRequest(filter.value.value, 'and', filter.filterOperator);
};

const isRelativeOperator = (operator: FilterOperator) => {
    switch (operator) {
        case 'TODAY':
        case 'YESTERDAY':
        case 'LAST-WEEK':
        case 'LAST-TWO-WEEKS':
        case 'LAST-MONTH':
        case 'LAST-QUARTER':
        case 'LAST-YEAR':
        case 'MTD':
        case 'QTD':
        case 'YTD':
        case 'ALL-TIME':
            return true;
        default:
            return false;
    }
};
